import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { Fragment, useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';

import InvoicesTab from '@features/enterpriseAccount/components/InvoicesTab/InvoicesTab';
import ManageBillingTab from '@features/enterpriseAccount/components/ManageBillingTab/ManageBillingTab';
import { successGreen, white } from '@styles/colors';
import EnterprisePaymentMethods from '@features/enterpriseAccount/components/EnterprisePaymentMethods/EnterprisePaymentMethods';
import { useGetTotalAggregate } from '@features/payments/hooks/useGetTotalAggregate';

type SettingTab = {
  label: string;
  isOpen: boolean;
  tabs: string[];
};

const initialOpenTabs = {
  'Billing & Upgrades': true,
};

const EnterpriseBilling = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { totalAggregate, isLoadingTotalAggregate } = useGetTotalAggregate();
  const hash = location.hash.replace('#', '').replace(/-/g, ' ');

  const [openTabs, setOpenTabs] = useState(initialOpenTabs);

  const settingTabs: SettingTab[] = [
    {
      label: 'Billing & Upgrades',
      isOpen: openTabs['Billing & Upgrades'],
      tabs: ['Payment Info', 'Manage Billing', 'Invoices'],
    },
  ];

  const allTabs = settingTabs.flatMap((tab) => tab.tabs);
  const [selectedTab, setSelectedTab] = useState<
    (typeof allTabs | 'WOTC' | 'Onboarding')[number]
  >(hash || allTabs[0]);

  const getTab = () => {
    switch (selectedTab) {
      case 'Manage Billing':
        return (
          <ManageBillingTab totalAggregate={totalAggregate?.objects ?? []} />
        );
      case 'Invoices':
        return <InvoicesTab />;
      case 'Payment Info':
        return <EnterprisePaymentMethods />;

      default:
        return null;
    }
  };

  useEffect(() => {
    if (!hash) {
      // If there's no hash, open the "User Settings" tab by default
      setOpenTabs((prev) => ({ ...prev, 'Manage Billing': true }));
      setSelectedTab('Payment Info'); // Set the default tab under "User Settings"
      return;
    }
    if (hash && allTabs.includes(hash)) {
      setSelectedTab(hash);

      const currentSettingTab = settingTabs.find((settingTab) =>
        settingTab.tabs.includes(hash),
      );

      if (currentSettingTab) {
        setOpenTabs((prev) => ({ ...prev, [currentSettingTab.label]: true }));
      }
    }
  }, [hash]);

  return (
    <>
      <Helmet>
        <title>Enterprise Billing | StaffedUp</title>
      </Helmet>
      <Stack component="section" spacing={5} pt={5} pb={4} px={3} pl={6}>
        <Stack
          p={3}
          borderRadius="8px"
          bgcolor={white}
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
        >
          <Typography variant="h3" fontSize="28px" fontWeight="bold">
            Enterprise Billing
          </Typography>
          {totalAggregate?.next_invoice_amount &&
            totalAggregate?.next_invoice_amount > 0 && (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  Next Invoice Amount:
                </Typography>
                {isLoadingTotalAggregate ? (
                  <CircularProgress size={24} />
                ) : (
                  <Typography
                    sx={{ color: successGreen }}
                    variant="h4"
                    fontWeight="bold"
                    color="text.success"
                  >
                    ${totalAggregate?.next_invoice_amount || 0}
                  </Typography>
                )}
              </Box>
            )}
        </Stack>
        <Grid container>
          <Grid
            item
            xs={12}
            sm={4}
            md={3}
            pr={{ xs: 0, sm: 3 }}
            pb={{ xs: 3, sm: 0 }}
            sx={{
              borderRadius: 8,
            }}
          >
            <List
              sx={{
                bgcolor: 'secondary.main',
                color: white,
                borderRadius: 8,
              }}
              component="nav"
            >
              {settingTabs.map((settingTab) => (
                <Fragment key={settingTab.label}>
                  <ListItem
                    button
                    onClick={() =>
                      setOpenTabs((prev) => ({
                        ...prev,
                        [settingTab.label]: !settingTab.isOpen,
                      }))
                    }
                    id={`parent-button-${settingTab.label.toLowerCase().replace(/\s+/g, '-') }`}
                  >
                    <ListItemText
                      primary={settingTab.label}
                      primaryTypographyProps={{
                        variant: 'h4',
                        fontSize: '16px',
                      }}
                    />
                    {settingTab.isOpen ? (
                      <ExpandLessIcon style={{ fontSize: '20px' }} />
                    ) : (
                      <ExpandMoreIcon style={{ fontSize: '20px' }} />
                    )}
                  </ListItem>
                  <Collapse in={settingTab.isOpen} timeout="auto" unmountOnExit>
                    <List
                      disablePadding
                      sx={{
                        bgcolor: white,
                        color: 'text.primary',
                      }}
                    >
                      {settingTab.tabs.map((tab) => (
                        <ListItem
                          key={tab}
                          button
                          sx={{ pl: 4, py: '5px' }}
                          onClick={() => {
                            setSelectedTab(tab);
                            navigate(`#${tab.replace(/\s+/g, '-')}`);
                          }}
                          selected={selectedTab === tab}
                          id={`child-button-${tab.toLowerCase().replace(/\s+/g, '-') }`}
                        >
                          <ListItemText
                            primary={tab}
                            primaryTypographyProps={{
                              variant: 'subtitle1',
                              fontSize: '14px',
                              fontWeight: '500',
                            }}
                          />
                          <KeyboardArrowRightIcon
                            style={{ fontSize: '20px' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </Fragment>
              ))}
            </List>
          </Grid>
          <Grid
            sx={{
              borderRadius: 8,
            }}
            item
            xs={12}
            sm={8}
            md={9}
          >
            {getTab()}
          </Grid>
        </Grid>
        <Stack></Stack>
      </Stack>
    </>
  );
};

export default EnterpriseBilling;
